import React, { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useTenantContext } from '../../context/TenantContext';
import { toast } from 'sonner';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { MediaSelector } from '../../components/media/MediaSelector';
import { invalidateTenantCache } from '../../services/tenantCache';
import { TenantTemplates } from '../../components/admin/TenantTemplates';
import { TenantGeneralConfig } from '../../components/admin/TenantGeneralConfig';
import { TenantSocialMedia } from '../../components/admin/TenantSocialMedia';
import { TenantProfessional } from '../../components/admin/TenantProfessional';
import { TenantColorsAndFeatures } from '../../components/admin/TenantColorsAndFeatures';
import { TenantPagesCustomization } from '../../components/admin/TenantPagesCustomization';
import { TenantConfigBackup } from '../../components/admin/TenantConfigBackup';
import { Settings, Save, RefreshCw, CheckCircle, Loader2, Globe, Mail, Image as ImageIcon, Phone, Palette, ToggleLeft, Facebook, X, Instagram, Linkedin, User, Share2, MessageSquare, LayoutGrid, HardDrive, CreditCard, ArrowUp, ArrowDown } from 'lucide-react';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/svg+xml"];
const ACCEPTED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".svg"];

export interface TenantConfigData {
  id: string;
  name: string;
  email: string;
  logo: string;
  domain_name: string;
  config: {
    year?: string;
    phone?: string;
    email2?: string;
    phone2?: string;
    phone3?: string;
    colors?: {
      primary?: string;
      secondary?: string;
      color1?: string;
      color2?: string;
      color3?: string;
      color4?: string;
    };
    features?: {
      shop?: boolean;
      blog?: boolean;
      events?: boolean;
      contests?: boolean;
    };
    social?: {
      facebook?: string;
      x?: string;
      instagram?: string;
      linkedin?: string;
      tiktok?: string;
      youtube?: string;
      whatsapp?: string;
      pinterest?: string;
      snapchat?: string;
    };
    templates?: {
      home?: string;
      contact?: string;
      about?: string;
      headers?: Record<string, string | undefined>; // { default?, home?, contact?, about? }
      footers?: Record<string, string | undefined>; // { default?, home?, contact?, about? }
      admin_headers?: Record<string, string | undefined>; // { default? }
      [key: string]: any;
    };
    bg?: string | undefined;
    [key: string]: any;
  };
}

export default function TenantConfigPage() {
  const { tenant, loading: tenantLoading } = useTenantContext();
  const [formData, setFormData] = useState<TenantConfigData>({
    id: '',
    name: '',
    email: '',
    logo: '',
    domain_name: '',
    config: {}
  });

  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      const allowed = ['config', 'templates', 'appearance', 'social', 'professional', 'pages', 'backups', 'payments'];
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      if (tabParam && allowed.includes(tabParam)) {
        return tabParam;
      }
      const saved = localStorage.getItem('tenant-config-page-active-tab:global');
      return saved && allowed.includes(saved) ? saved : 'config';
    } catch {
      return 'config';
    }
  });
  // Persistance de l'onglet sélectionné (par tenant)
  const tabStorageKey = useMemo(() => `tenant-config-page-active-tab:${tenant?.id ?? 'global'}`, [tenant?.id]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | undefined>(undefined);
  const [iconPreview, setIconPreview] = useState<string | undefined>(undefined);
  const [bgPreview, setBgPreview] = useState<string | undefined>(undefined);
  const [customFieldName, setCustomFieldName] = useState<string>('');
  const [customFieldValue, setCustomFieldValue] = useState<string>('');
  const [showMediaSelector, setShowMediaSelector] = useState(false);
  const [mediaSelectorType, setMediaSelectorType] = useState<'logo' | 'icon' | 'bg'>('logo');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);
  const bgInputRef = useRef<HTMLInputElement>(null);
  const [currentDomain, setCurrentDomain] = useState<string>('');
  const [domainInfo, setDomainInfo] = useState<{ id: number; domain_name: string; is_primary: boolean } | null>(null);
  
  // États pour la configuration des moyens de paiement
  const [paymentConfig, setPaymentConfig] = useState<Array<{
    provider_id: string;
    name: string;
    logo_url: string;
    is_enabled: boolean;
    display_order: number;
  }>>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [isSavingPayments, setIsSavingPayments] = useState(false);

  // États pour le système de sélection de templates à deux niveaux
  const [selectedPage, setSelectedPage] = useState<string>('home');
  const [pendingTemplateChanges, setPendingTemplateChanges] = useState<Record<string, string>>({});

  useEffect(() => {
    if (tenant) {
      const domain = tenant.domain_name;
      let config = tenant.config || {};
      
      // Set default social media values if not present
      if (!config.social) config.social = {};
      if (!config.social.facebook) config.social.facebook = `https://facebook.com/`;
      if (!config.social.x) config.social.x = `https://x.com/`;
      if (!config.social.instagram) config.social.instagram = `https://instagram.com/`;
      if (!config.social.linkedin) config.social.linkedin = `https://linkedin.com/company/`;
      if (!config.social.tiktok) config.social.tiktok = `https://tiktok.com/@`;
      if (!config.social.youtube) config.social.youtube = `https://youtube.com/@`;
      if (!config.social.pinterest) config.social.pinterest = `https://pinterest.com/`;
      if (!config.social.snapchat) config.social.snapchat = `https://snapchat.com/add/`;
      
      setFormData({
        id: tenant.id,
        name: tenant.name || '',
        email: tenant.email || '',
        logo: tenant.logo || '',
        domain_name: domain || '',
        config: config
      });
      
      if (tenant.logo) {
        setImagePreview(tenant.logo);
      }
      
      if (config.icon) {
        setIconPreview(config.icon);
      }
      
      if (config.bg) {
        setBgPreview(config.bg);
      }
    }
  }, [tenant]);

  // Charger l'onglet actif sauvegardé au montage (ne pas écraser si un param 'tab' est présent dans l'URL)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      if (tabParam) return; // priorité à l'URL
      const saved = localStorage.getItem(tabStorageKey);
      const allowed = ['config', 'templates', 'appearance', 'social', 'professional', 'pages', 'backups', 'payments'];
      if (saved && allowed.includes(saved)) {
        setActiveTab(saved);
      }
    } catch {}
  }, [tabStorageKey]);

  // Persister l'onglet actif à chaque changement
  useEffect(() => {
    try {
      localStorage.setItem(tabStorageKey, activeTab);
      // Synchroniser aussi dans l'URL (sans recharger la page)
      const url = new URL(window.location.href);
      url.searchParams.set('tab', activeTab);
      window.history.replaceState({}, '', url.toString());
    } catch {}
  }, [activeTab, tabStorageKey]);

  // Helper: changer d'onglet + persister immédiatement + MAJ URL
  const switchTab = (next: 'config' | 'templates' | 'appearance' | 'social' | 'professional' | 'pages' | 'backups' | 'payments') => {
    try {
      localStorage.setItem(tabStorageKey, next);
      const url = new URL(window.location.href);
      url.searchParams.set('tab', next);
      window.history.replaceState({}, '', url.toString());
    } catch {}
    setActiveTab(next);
  };

  // Charger les informations du domaine courant (id, is_primary) via RPC pour bypasser RLS
  useEffect(() => {
    async function loadCurrentDomainInfo() {
      try {
        const hostname = window.location.hostname;
        setCurrentDomain(hostname);
        if (!tenant?.id) return;
        const numericId = parseInt(tenant.id, 10);
        if (isNaN(numericId)) return;

        const { data, error } = await supabase
          .rpc('get_tenant_domains', { tenant_id_param: numericId });

        if (error) {
          console.error('Erreur RPC get_tenant_domains:', error);
          return;
        }

        const match = (data || []).find((d: any) => (d.domain_name || '').toLowerCase() === hostname.toLowerCase());
        if (match) {
          setDomainInfo({ id: match.id, domain_name: match.domain_name, is_primary: !!match.is_primary });
        } else {
          console.warn('Aucun domaine ne correspond au hostname courant:', hostname);
        }
      } catch (e) {
        console.error('Erreur lors du chargement des infos domaine:', e);
      }
    }

    loadCurrentDomainInfo();
  }, [tenant]);

  // Charger la configuration des moyens de paiement pour ce tenant
  useEffect(() => {
    async function loadPaymentConfig() {
      if (!tenant?.id) return;
      
      setIsLoadingPayments(true);
      try {
        const tenantId = parseInt(tenant.id, 10);
        if (isNaN(tenantId)) return;

        // Charger les providers globaux actifs
        const { data: globalProviders, error: providersError } = await supabase
          .from('payment_providers')
          .select('id, name, logo_url, is_active, display_order')
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (providersError) {
          console.error('Erreur chargement payment_providers:', providersError);
          return;
        }

        // Charger la config spécifique au tenant
        const { data: tenantProviders, error: tenantError } = await supabase
          .from('tenant_payment_providers')
          .select('provider_id, is_enabled, display_order')
          .eq('tenant_id', tenantId);

        if (tenantError) {
          console.error('Erreur chargement tenant_payment_providers:', tenantError);
        }

        // Fusionner les configs
        const tenantConfigMap = new Map(
          (tenantProviders || []).map((tp: any) => [tp.provider_id, tp])
        );

        const merged = (globalProviders || []).map((gp: any) => {
          const tenantCfg = tenantConfigMap.get(gp.id);
          return {
            provider_id: gp.id,
            name: gp.name,
            logo_url: gp.logo_url,
            is_enabled: tenantCfg ? tenantCfg.is_enabled : true,
            display_order: tenantCfg ? tenantCfg.display_order : gp.display_order
          };
        });

        // Trier par display_order
        merged.sort((a: any, b: any) => a.display_order - b.display_order);
        setPaymentConfig(merged);
      } catch (e) {
        console.error('Erreur lors du chargement de la config paiements:', e);
      } finally {
        setIsLoadingPayments(false);
      }
    }

    loadPaymentConfig();
  }, [tenant]);

  // Sauvegarder la configuration des moyens de paiement
  const handleSavePaymentConfig = async () => {
    if (!tenant?.id) return;
    
    setIsSavingPayments(true);
    try {
      // Debug: vérifier la valeur de tenant.id
      console.log('[PaymentConfig] tenant.id brut:', tenant.id, typeof tenant.id);
      
      const tenantId = parseInt(tenant.id, 10);
      console.log('[PaymentConfig] tenantId parsé:', tenantId);
      
      if (isNaN(tenantId)) {
        toast.error('ID tenant invalide');
        return;
      }

      // Supprimer l'ancienne config
      const { error: deleteError } = await supabase
        .from('tenant_payment_providers')
        .delete()
        .eq('tenant_id', tenantId);

      if (deleteError) {
        console.error('Erreur suppression config paiements:', deleteError);
        toast.error('Erreur lors de la suppression de l\'ancienne configuration');
        return;
      }

      // Insérer la nouvelle config
      const rows = paymentConfig.map((pc, index) => ({
        tenant_id: tenantId,
        provider_id: pc.provider_id,
        is_enabled: pc.is_enabled,
        display_order: index
      }));

      const { error: insertError } = await supabase
        .from('tenant_payment_providers')
        .insert(rows);

      if (insertError) {
        console.error('Erreur insertion config paiements:', insertError);
        toast.error('Erreur lors de la sauvegarde de la configuration');
        return;
      }

      toast.success('Configuration des moyens de paiement sauvegardée');
    } catch (e) {
      console.error('Erreur sauvegarde config paiements:', e);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSavingPayments(false);
    }
  };

  // Déplacer un provider vers le haut ou le bas
  const movePaymentProvider = (index: number, direction: 'up' | 'down') => {
    setPaymentConfig(prev => {
      const newConfig = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= newConfig.length) return prev;
      
      [newConfig[index], newConfig[targetIndex]] = [newConfig[targetIndex], newConfig[index]];
      return newConfig;
    });
  };

  // Toggle activation d'un provider
  const togglePaymentProvider = (index: number) => {
    setPaymentConfig(prev => {
      const newConfig = [...prev];
      newConfig[index] = { ...newConfig[index], is_enabled: !newConfig[index].is_enabled };
      return newConfig;
    });
  };

  // Lister les templates disponibles via Vite (build-time)
  const templateModules = useMemo(() => {
    try {
      return import.meta.glob('/src/templates/**/*.{tsx,jsx,ts,js}', { eager: false });
    } catch (e) {
      console.warn('Aucun template détecté dans /src/templates');
      return {} as Record<string, unknown>;
    }
  }, []);

  const templatePaths = useMemo(() => Object.keys(templateModules || {}), [templateModules]);

  const homeOptions = useMemo(() =>
    templatePaths
      .filter(p => /\/home\//i.test(p) || /home(\.|-)/i.test(p))
      .map(p => ({ value: p, label: p.replace('/src/templates/', '') })),
  [templatePaths]);

  const contactOptions = useMemo(() =>
    templatePaths
      .filter(p => /\/contact\//i.test(p) || /contact(\.|-)/i.test(p))
      .map(p => ({ value: p, label: p.replace('/src/templates/', '') })),
  [templatePaths]);

  const aboutOptions = useMemo(() =>
    templatePaths
      .filter(p => /\/about\//i.test(p) || /about(\.|-)/i.test(p))
      .map(p => ({ value: p, label: p.replace('/src/templates/', '') })),
  [templatePaths]);

  // Modules spécifiques headers/footers
  const headerModules = useMemo(() => {
    try {
      return import.meta.glob('/src/templates/headers/**/*.{tsx,jsx,ts,js}', { eager: false });
    } catch (e) {
      console.warn('Aucun header détecté dans /src/templates/headers');
      return {} as Record<string, unknown>;
    }
  }, []);

  const footerModules = useMemo(() => {
    try {
      return import.meta.glob('/src/templates/footers/**/*.{tsx,jsx,ts,js}', { eager: false });
    } catch (e) {
      console.warn('Aucun footer détecté dans /src/templates/footers');
      return {} as Record<string, unknown>;
    }
  }, []);

  // Modules spécifiques headers ADMIN
  const adminHeaderModules = useMemo(() => {
    try {
      return import.meta.glob('/src/templates/admin-headers/**/*.{tsx,jsx,ts,js}', { eager: false });
    } catch (e) {
      console.warn('Aucun header admin détecté dans /src/templates/admin-headers');
      return {} as Record<string, unknown>;
    }
  }, []);

  const headerOptions = useMemo(() => Object.keys(headerModules).map(p => ({ value: p, label: p.replace('/src/templates/headers/', '') })), [headerModules]);
  const footerOptions = useMemo(() => Object.keys(footerModules).map(p => ({ value: p, label: p.replace('/src/templates/footers/', '') })), [footerModules]);
  const adminHeaderOptions = useMemo(() => Object.keys(adminHeaderModules).map(p => ({ value: p, label: p.replace('/src/templates/admin-headers/', '') })), [adminHeaderModules]);

  // Définir un template pour une page donnée (sauvé dans config.templates)
  // Nouvelle fonction pour gérer les changements de templates en attente
  const handlePendingTemplateChange = (page: string, templatePath: string) => {
    setPendingTemplateChanges(prev => ({
      ...prev,
      [page]: templatePath
    }));
  };

  // Appliquer les changements de templates en attente au formData
  const applyPendingTemplateChanges = () => {
    setFormData(prev => {
      const newConfig = { ...prev.config };
      const templates = { ...(newConfig.templates || {}) } as Record<string, string>;
      
      Object.entries(pendingTemplateChanges).forEach(([page, templatePath]) => {
        if (!templatePath) {
          delete templates[page];
        } else {
          templates[page] = templatePath;
        }
      });
      
      newConfig.templates = templates;
      return { ...prev, config: newConfig };
    });
    setPendingTemplateChanges({});
  };

  const setTemplateSelection = (page: 'home' | 'contact' | 'about', pathValue: string) => {
    setFormData(prev => {
      const newConfig = { ...prev.config };
      const templates = { ...(newConfig.templates || {}) } as Record<string, string>;
      if (!pathValue) {
        delete templates[page];
      } else {
        templates[page] = pathValue;
      }
      newConfig.templates = templates;
      return { ...prev, config: newConfig };
    });
  };

  const setHeaderTemplateSelection = (page: 'default' | 'home' | 'contact' | 'about', pathValue: string) => {
    setFormData(prev => {
      const newConfig = { ...prev.config };
      const templates = { ...(newConfig.templates || {}) } as any;
      const headers = { ...(templates.headers || {}) } as Record<string, string | undefined>;
      if (!pathValue) {
        delete headers[page];
      } else {
        headers[page] = pathValue;
      }
      templates.headers = headers;
      newConfig.templates = templates;
      return { ...prev, config: newConfig };
    });
  };

  const setAdminHeaderTemplateSelection = (page: 'default', pathValue: string) => {
    setFormData(prev => {
      const newConfig = { ...prev.config };
      const templates = { ...(newConfig.templates || {}) } as any;
      const adminHeaders = { ...(templates.admin_headers || {}) } as Record<string, string | undefined>;
      if (!pathValue) {
        delete adminHeaders[page];
      } else {
        adminHeaders[page] = pathValue;
      }
      templates.admin_headers = adminHeaders;
      newConfig.templates = templates;
      return { ...prev, config: newConfig };
    });
  };

  const setFooterTemplateSelection = (page: 'default' | 'home' | 'contact' | 'about', pathValue: string) => {
    setFormData(prev => {
      const newConfig = { ...prev.config };
      const templates = { ...(newConfig.templates || {}) } as any;
      const footers = { ...(templates.footers || {}) } as Record<string, string | undefined>;
      if (!pathValue) {
        delete footers[page];
      } else {
        footers[page] = pathValue;
      }
      templates.footers = footers;
      newConfig.templates = templates;
      return { ...prev, config: newConfig };
    });
  };

  const updateServicesConfig = (servicesConfig: { folder: string; routes: Record<string, string> }) => {
    setFormData(prev => {
      const newConfig = { ...prev.config };
      newConfig.services = servicesConfig;
      console.log('✅ Config services mise à jour dans formData:', servicesConfig);
      return { ...prev, config: newConfig };
    });
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConfigChange = (path: string, value: any) => {
    setFormData(prev => {
      const newConfig = { ...prev.config };
      const keys = path.split('.');
      let current = newConfig;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      
      return {
        ...prev,
        config: newConfig
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const numericId = parseInt(formData.id, 10);
      if (isNaN(numericId)) throw new Error('ID du tenant invalide');
      
      let logoUrl = formData.logo;
      let iconUrl = formData.config.icon;
      let bgUrl = formData.config.bg;
      
      // Si l'image est en base64 (nouvelle image uploadée), on l'upload sur Supabase Storage
      if (imagePreview && imagePreview.startsWith('data:')) {
        // Convertir l'image base64 en fichier
        const base64Response = await fetch(imagePreview);
        const blob = await base64Response.blob();
        const file = new File([blob], `tenant-${numericId}-logo.${blob.type.split('/')[1]}`, { type: blob.type });
        
        // Supprimer l'ancien logo si nécessaire
        if (formData.logo && formData.logo.includes('storage.supabase.co')) {
          try {
            const oldFilePath = formData.logo.split('/').pop();
            if (oldFilePath) {
              await supabase.storage
                .from('partners')
                .remove([oldFilePath]);
            }
          } catch (removeError) {
            console.error("Erreur lors de la suppression de l'ancien logo:", removeError);
          }
        }
        
        // Générer un nom de fichier unique
        const fileExt = file.type.split('/')[1];
        const fileName = `tenant-${numericId}-logo-${Date.now()}.${fileExt}`;
        const filePath = `tenants/${fileName}`;
        
        // Upload du fichier
        const { error: uploadError } = await supabase.storage
          .from('partners')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true
          });
          
        if (uploadError) throw uploadError;
        
        // Récupérer l'URL publique
        const { data: publicUrlData } = supabase.storage
          .from('partners')
          .getPublicUrl(filePath);
          
        logoUrl = publicUrlData.publicUrl;
      }
      
      // Si l'icône est en base64 (nouvelle icône uploadée), on l'upload sur Supabase Storage
      if (iconPreview && iconPreview.startsWith('data:')) {
        // Convertir l'image base64 en fichier
        const base64Response = await fetch(iconPreview);
        const blob = await base64Response.blob();
        const file = new File([blob], `tenant-${numericId}-icon.${blob.type.split('/')[1]}`, { type: blob.type });
        
        // Supprimer l'ancienne icône si nécessaire
        if (formData.config.icon && formData.config.icon.includes('storage.supabase.co')) {
          try {
            const oldFilePath = formData.config.icon.split('/').pop();
            if (oldFilePath) {
              await supabase.storage
                .from('partners')
                .remove([oldFilePath]);
            }
          } catch (removeError) {
            console.error("Erreur lors de la suppression de l'ancienne icône:", removeError);
          }
        }
        
        // Générer un nom de fichier unique
        const fileExt = file.type.split('/')[1];
        const fileName = `tenant-${numericId}-icon-${Date.now()}.${fileExt}`;
        const filePath = `tenants/${fileName}`;
        
        // Upload du fichier
        const { error: uploadError } = await supabase.storage
          .from('partners')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true
          });
          
        if (uploadError) throw uploadError;
        
        // Récupérer l'URL publique
        const { data: publicUrlData } = supabase.storage
          .from('partners')
          .getPublicUrl(filePath);
          
        iconUrl = publicUrlData.publicUrl;
        
        // Mettre à jour l'URL de l'icône dans le formulaire
        handleConfigChange('icon', iconUrl);
      }
      
      // Si le background est en base64 (nouveau background uploadé), on l'upload sur Supabase Storage
      if (bgPreview && bgPreview.startsWith('data:')) {
        // Convertir l'image base64 en fichier
        const base64Response = await fetch(bgPreview);
        const blob = await base64Response.blob();
        const file = new File([blob], `tenant-${numericId}-bg.${blob.type.split('/')[1]}`, { type: blob.type });
        
        // Supprimer l'ancien background si nécessaire
        if (formData.config.bg && formData.config.bg.includes('storage.supabase.co')) {
          try {
            const oldFilePath = formData.config.bg.split('/').pop();
            if (oldFilePath) {
              await supabase.storage
                .from('partners')
                .remove([oldFilePath]);
            }
          } catch (removeError) {
            console.error("Erreur lors de la suppression de l'ancien background:", removeError);
          }
        }
        
        // Générer un nom de fichier unique
        const fileExt = file.type.split('/')[1];
        const fileName = `tenant-${numericId}-bg-${Date.now()}.${fileExt}`;
        const filePath = `tenants/${fileName}`;
        
        // Upload du fichier
        const { error: uploadError } = await supabase.storage
          .from('partners')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true
          });
          
        if (uploadError) throw uploadError;
        
        // Récupérer l'URL publique
        const { data: publicUrlData } = supabase.storage
          .from('partners')
          .getPublicUrl(filePath);
          
        bgUrl = publicUrlData.publicUrl;
        
        // Mettre à jour l'URL du background dans le formulaire
        handleConfigChange('bg', bgUrl);
      }
      
      // Construire la configuration spécifique au domaine (overrides)
      const domainConfig = {
        ...formData.config,
        logo: logoUrl,
        email: formData.email,
        name: formData.name,
      };

      if (!domainInfo) {
        throw new Error("Domaine courant introuvable pour ce tenant. Assurez-vous que le domaine est bien ajouté au tenant.");
      }

      // Sauvegarder uniquement sur le domaine courant via RPC (bypass RLS)
      const { data: updateOk, error: updateErr } = await supabase
        .rpc('update_tenant_domain', {
          domain_id_param: domainInfo.id,
          domain_name_param: domainInfo.domain_name,
          is_primary_param: domainInfo.is_primary,
          config_param: domainConfig
        });

      if (updateErr) throw updateErr;
      if (!updateOk) throw new Error('La mise à jour du domaine a échoué');
      
      // Invalider le cache tenant après la sauvegarde
      invalidateTenantCache(formData.id);
      
      toast.success(`Configuration du domaine ${currentDomain || domainInfo.domain_name} sauvegardée avec succès`);
      // Forcer la préservation de l'onglet courant (tab) et du sous-onglet (subtab) après rechargement
      try {
        const url = new URL(window.location.href);
        const allowed = ['config', 'templates', 'appearance', 'social', 'professional', 'pages', 'backups', 'payments'];
        const savedMain = localStorage.getItem(tabStorageKey);
        if (savedMain && allowed.includes(savedMain)) {
          url.searchParams.set('tab', savedMain);
        }
        const subKey = `tenant-admin-config-active-tab:${tenant?.id ?? 'global'}`;
        const savedSub = localStorage.getItem(subKey);
        if (savedSub && (savedSub === 'colors' || savedSub === 'header')) {
          url.searchParams.set('subtab', savedSub);
        }
        window.location.href = url.toString();
      } catch {
        // Fallback
        window.location.reload();
      }
    } catch (error: any) {
      toast.error('Erreur lors de la sauvegarde: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setSaving(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    let file: File | null = null;
    
    if ('dataTransfer' in e) {
      // Gestion du glisser-déposer
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        file = e.dataTransfer.files[0];
      }
    } else if (e.target.files && e.target.files.length > 0) {
      // Gestion du clic sur l'input file
      file = e.target.files[0];
    }
    
    if (file) {
      // Vérification du type de fichier
      const fileType = file.type;
      if (!ACCEPTED_IMAGE_TYPES.includes(fileType)) {
        toast.error('Format non supporté. Veuillez utiliser un fichier image (JPEG, JPG, PNG, WEBP ou SVG)');
        return;
      }
      
      // Vérification de la taille du fichier
      if (file.size > MAX_FILE_SIZE) {
        toast.error('Fichier trop volumineux. La taille maximale autorisée est de 5 Mo');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setImagePreview(result);
        // Mettre à jour l'URL du logo dans le formulaire pour la prévisualisation
        // L'upload réel se fera lors de la sauvegarde
        setFormData(prev => ({
          ...prev,
          logo: result
        }));
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    let file: File | null = null;
    
    if ('dataTransfer' in e) {
      // Gestion du glisser-déposer
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        file = e.dataTransfer.files[0];
      }
    } else if (e.target.files && e.target.files.length > 0) {
      // Gestion du clic sur l'input file
      file = e.target.files[0];
    }
    
    if (file) {
      // Vérification du type de fichier
      const fileType = file.type;
      if (!ACCEPTED_IMAGE_TYPES.includes(fileType)) {
        toast.error('Format non supporté. Veuillez utiliser un fichier image (JPEG, JPG, PNG, WEBP ou SVG)');
        return;
      }
      
      // Vérification de la taille du fichier
      if (file.size > MAX_FILE_SIZE) {
        toast.error('Fichier trop volumineux. La taille maximale autorisée est de 5 Mo');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setIconPreview(result);
        // Mettre à jour l'URL de l'icône dans le formulaire pour la prévisualisation
        // L'upload réel se fera lors de la sauvegarde
        handleConfigChange('icon', result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBgChange = (e: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    let file: File | null = null;
    
    if ('dataTransfer' in e) {
      // Gestion du glisser-déposer
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        file = e.dataTransfer.files[0];
      }
    } else if (e.target.files && e.target.files.length > 0) {
      // Gestion du clic sur l'input file
      file = e.target.files[0];
    }
    
    if (file) {
      // Vérification du type de fichier
      const fileType = file.type;
      if (!ACCEPTED_IMAGE_TYPES.includes(fileType)) {
        toast.error('Format non supporté. Veuillez utiliser un fichier image (JPEG, JPG, PNG, WEBP ou SVG)');
        return;
      }
      
      // Vérification de la taille du fichier
      if (file.size > MAX_FILE_SIZE) {
        toast.error('Fichier trop volumineux. La taille maximale autorisée est de 5 Mo');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setBgPreview(result);
        // Mettre à jour l'URL du background dans le formulaire pour la prévisualisation
        // L'upload réel se fera lors de la sauvegarde
        handleConfigChange('bg', result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMediaSelect = (url: string) => {
    if (mediaSelectorType === 'logo') {
      setImagePreview(url);
      setFormData(prev => ({ ...prev, logo: url }));
    } else if (mediaSelectorType === 'icon') {
      setIconPreview(url);
      setFormData(prev => ({ ...prev, config: { ...prev.config, icon: url } }));
    } else if (mediaSelectorType === 'bg') {
      setBgPreview(url);
      setFormData(prev => ({ ...prev, config: { ...prev.config, bg: url } }));
    }
    setShowMediaSelector(false);
  };

  const openMediaSelector = (type: 'logo' | 'icon' | 'bg') => {
    setMediaSelectorType(type);
    setShowMediaSelector(true);
  };

  const removeImage = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce logo ?')) {
      return;
    }
    
    try {
      // Supprimer l'image du stockage si elle existe
      if (formData.logo && formData.logo.includes('storage.supabase.co')) {
        const fileName = formData.logo.split('/').pop();
        if (fileName) {
          await supabase.storage
            .from('partners')
            .remove([fileName]);
        }
      }
      
      // Mettre à jour l'état local
      setImagePreview(undefined);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Mettre à jour l'URL du logo dans le formulaire
      setFormData(prev => ({
        ...prev,
        logo: ''
      }));
      
      toast.success('Logo supprimé avec succès');
    } catch (error: any) {
      console.error("Erreur lors de la suppression du logo:", error);
      toast.error("Une erreur est survenue lors de la suppression du logo: " + (error.message || 'Erreur inconnue'));
    }
  };
  
  const removeIcon = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette icône ?')) {
      return;
    }
    
    try {
      // Supprimer l'image du stockage si elle existe
      if (formData.config.icon && formData.config.icon.includes('storage.supabase.co')) {
        const fileName = formData.config.icon.split('/').pop();
        if (fileName) {
          await supabase.storage
            .from('partners')
            .remove([fileName]);
        }
      }
      
      // Mettre à jour l'état local
      setIconPreview(undefined);
      if (iconInputRef.current) {
        iconInputRef.current.value = '';
      }
      
      // Mettre à jour l'URL de l'icône dans le formulaire
      handleConfigChange('icon', '');
      
      toast.success('Icône supprimée avec succès');
    } catch (error: any) {
      console.error("Erreur lors de la suppression de l'icône:", error);
      toast.error("Une erreur est survenue lors de la suppression de l'icône: " + (error.message || 'Erreur inconnue'));
    }
  };

  const removeBg = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce background ?')) {
      return;
    }
    
    try {
      // Supprimer l'image du stockage si elle existe
      if (formData.config.bg && formData.config.bg.includes('storage.supabase.co')) {
        const fileName = formData.config.bg.split('/').pop();
        if (fileName) {
          await supabase.storage
            .from('partners')
            .remove([fileName]);
        }
      }
      
      // Mettre à jour l'état local
      setBgPreview(undefined);
      if (bgInputRef.current) {
        bgInputRef.current.value = '';
      }
      
      // Mettre à jour l'URL du background dans le formulaire
      handleConfigChange('bg', '');
      
      toast.success('Background supprimé avec succès');
    } catch (error: any) {
      console.error("Erreur lors de la suppression du background:", error);
      toast.error("Une erreur est survenue lors de la suppression du background: " + (error.message || 'Erreur inconnue'));
    }
  };

  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de la configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Settings className="h-6 w-6 mr-2 text-gray-500" />
            <h1 className="text-2xl font-bold text-white">Configuration du Site</h1>
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="flex items-center px-3 py-1.5 text-sm text-gray-300 bg-white/5 border border-white/10 rounded-md hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Actualiser
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-3 py-1.5 text-sm text-white bg-gradient-to-r from-amber-800 to-amber-700 border border-amber-900 rounded-md hover:from-amber-900 hover:to-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>

        <div className="flex bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl shadow-xl overflow-hidden">
          {/* Menu vertical à gauche */}
          <div className="w-64 border-r border-white/10 bg-black/20 p-4">
            <div className="px-4 py-3 border-b border-white/10 mb-4">
              <div>
                <p className="text-gray-300 flex items-center">
                  <span className="mr-2">ID du tenant:</span> 
                  <span className="font-semibold text-amber-400 bg-black/30 px-2 py-0.5 rounded-md border border-white/10">{formData.id}</span>
                </p>
                <p className="text-gray-300 flex items-center mt-1">
                  <span className="mr-2">Domaine:</span> 
                  <span className="font-mono text-sm text-green-400 bg-black/30 px-2 py-0.5 rounded-md border border-white/10">{currentDomain || formData.domain_name}</span>
                </p>
              </div>
            </div>
            
            <nav className="space-y-1">
              <button 
                onClick={() => switchTab('config')} 
                className={`w-full flex items-center px-4 py-2.5 text-left rounded-lg transition-colors ${activeTab === 'config' ? 'bg-green-500/20 text-green-400' : 'text-gray-400 hover:bg-white/5'}`}
              >
                <Settings className="h-5 w-5 mr-3" />
                Configuration générale
              </button>
              
              <button 
                onClick={() => switchTab('templates')} 
                className={`w-full flex items-center px-4 py-2.5 text-left rounded-lg transition-colors ${activeTab === 'templates' ? 'bg-green-500/20 text-green-400' : 'text-gray-400 hover:bg-white/5'}`}
              >
                <LayoutGrid className="h-5 w-5 mr-3" />
                Templates
              </button>
              
              <button 
                onClick={() => switchTab('pages')} 
                className={`w-full flex items-center px-4 py-2.5 text-left rounded-lg transition-colors ${activeTab === 'pages' ? 'bg-green-500/20 text-green-400' : 'text-gray-400 hover:bg-white/5'}`}
              >
                <MessageSquare className="h-5 w-5 mr-3" />
                Pages
              </button>
              
              <button 
                onClick={() => switchTab('appearance')} 
                className={`w-full flex items-center px-4 py-2.5 text-left rounded-lg transition-colors ${activeTab === 'appearance' ? 'bg-green-500/20 text-green-400' : 'text-gray-400 hover:bg-white/5'}`}
              >
                <Palette className="h-5 w-5 mr-3" />
                Apparence & Fonctionnalités
              </button>
              
              <button 
                onClick={() => switchTab('social')} 
                className={`w-full flex items-center px-4 py-2.5 text-left rounded-lg transition-colors ${activeTab === 'social' ? 'bg-green-500/20 text-green-400' : 'text-gray-400 hover:bg-white/5'}`}
              >
                <Share2 className="h-5 w-5 mr-3" />
                Réseaux sociaux
              </button>
              
              <button 
                onClick={() => switchTab('professional')} 
                className={`w-full flex items-center px-4 py-2.5 text-left rounded-lg transition-colors ${activeTab === 'professional' ? 'bg-green-500/20 text-green-400' : 'text-gray-400 hover:bg-white/5'}`}
              >
                <User className="h-5 w-5 mr-3" />
                Professionnel
              </button>
              
              <button 
                onClick={() => switchTab('payments')} 
                className={`w-full flex items-center px-4 py-2.5 text-left rounded-lg transition-colors ${activeTab === 'payments' ? 'bg-green-500/20 text-green-400' : 'text-gray-400 hover:bg-white/5'}`}
              >
                <CreditCard className="h-5 w-5 mr-3" />
                Paiements
              </button>
              
              <button 
                onClick={() => switchTab('backups')} 
                className={`w-full flex items-center px-4 py-2.5 text-left rounded-lg transition-colors ${activeTab === 'backups' ? 'bg-green-500/20 text-green-400' : 'text-gray-400 hover:bg-white/5'}`}
              >
                <HardDrive className="h-5 w-5 mr-3" />
                Sauvegardes
              </button>
            </nav>
          </div>
          
          {/* Contenu principal */}
          <div className="flex-1">
            <div className="px-6 py-4 border-b border-white/10 bg-gradient-to-r from-black/40 to-black/20">
              <h2 className="text-xl font-medium text-white">
                {activeTab === 'config' && 'Informations & Configuration'}
                {activeTab === 'templates' && 'Templates des pages'}
                {activeTab === 'pages' && 'Pages personnalisées'}
                {activeTab === 'appearance' && 'Apparence & Fonctionnalités'}
                {activeTab === 'social' && 'Réseaux sociaux'}
                {activeTab === 'professional' && 'Professionnel'}
                {activeTab === 'payments' && 'Moyens de paiement'}
                {activeTab === 'backups' && 'Sauvegardes & Restauration'}
              </h2>
            </div>

            <div className="p-6 space-y-8 text-gray-200">
              {/* Onglet Professionnel */}
              {activeTab === 'professional' && (
                <TenantProfessional
                  formData={formData}
                  customFieldName={customFieldName}
                  setCustomFieldName={setCustomFieldName}
                  customFieldValue={customFieldValue}
                  setCustomFieldValue={setCustomFieldValue}
                  handleConfigChange={handleConfigChange}
                />
              )}
              {activeTab === 'professional' && (
                <div className="mt-6 text-xs text-gray-600 opacity-50 border-t border-white/5 pt-2">
                  📄 src/components/admin/TenantProfessional.tsx
                </div>
              )}
              
              {/* Onglet Templates */}
              {activeTab === 'templates' && (
                <TenantTemplates
                  formData={formData}
                  selectedPage={selectedPage}
                  setSelectedPage={setSelectedPage}
                  pendingTemplateChanges={pendingTemplateChanges}
                  setPendingTemplateChanges={setPendingTemplateChanges}
                  templatePaths={templatePaths}
                  headerOptions={headerOptions}
                  footerOptions={footerOptions}
                  handlePendingTemplateChange={handlePendingTemplateChange}
                  applyPendingTemplateChanges={applyPendingTemplateChanges}
                  setHeaderTemplateSelection={setHeaderTemplateSelection}
                  setFooterTemplateSelection={setFooterTemplateSelection}
                  adminHeaderOptions={adminHeaderOptions}
                  setAdminHeaderTemplateSelection={setAdminHeaderTemplateSelection}
                  updateServicesConfig={updateServicesConfig}
                />
              )}
              {activeTab === 'templates' && (
                <div className="mt-6 text-xs text-gray-600 opacity-50 border-t border-white/5 pt-2">
                  📄 src/components/admin/TenantTemplates.tsx
                </div>
              )}

                           {/* Onglet Configuration */}
                           {activeTab === 'config' && (
                <section>
                  {/* Section Informations de base */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium text-white mb-6 border-b border-white/10 pb-2">Informations de base</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Nom du site <span className="text-gray-500 text-xs">(tenant.name)</span>
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Nom de domaine <span className="text-gray-500 text-xs">(tenant.domain_name)</span>
                        </label>
                        <input
                          type="text"
                          value={formData.domain_name}
                          onChange={(e) => handleInputChange('domain_name', e.target.value)}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Email 1 <span className="text-gray-500 text-xs">(tenant.email)</span>
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Email 2 <span className="text-gray-500 text-xs">(tenant.config.email2)</span>
                        </label>
                        <input
                          type="email"
                          value={formData.config.email2 || ''}
                          onChange={(e) => handleConfigChange('email2', e.target.value)}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Logo du site <span className="text-gray-500 text-xs">(tenant.logo)</span>
                        </label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept={ACCEPTED_IMAGE_TYPES.join(',')}
                          onChange={handleImageChange}
                          className="hidden"
                        />
                        
                        {imagePreview ? (
                          <div className="relative w-full h-48 bg-white/10 rounded-lg overflow-hidden">
                            <img
                              src={imagePreview}
                              alt="Logo preview"
                              className="w-full h-full object-contain"
                            />
                            <button
                              type="button"
                              className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-600 text-white p-1 rounded-full"
                              onClick={removeImage}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div 
                            className="border-2 border-dashed border-white/10 rounded-lg p-8 text-center bg-white/5 hover:border-white/20 transition-colors cursor-pointer"
                            onDrop={handleImageChange}
                            onDragOver={handleDragOver}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <ImageIcon className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                            <p className="text-sm text-gray-400 mb-2">
                              Glissez-déposez un logo ou cliquez pour sélectionner
                            </p>
                            <p className="text-xs text-gray-500">
                              SVG, PNG, JPG ou WEBP (max. 5MB)
                            </p>
                          </div>
                        )}
                        
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="px-3 py-2 bg-white/5 border border-white/10 rounded-md text-gray-300 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors flex items-center justify-center"
                          >
                            <ImageIcon className="h-4 w-4 mr-2" />
                            Uploader
                          </button>
                          <button
                            type="button"
                            onClick={() => openMediaSelector('logo')}
                            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors flex items-center justify-center"
                          >
                            <ImageIcon className="h-4 w-4 mr-2" />
                            Médiathèque
                          </button>
                        </div>
                        
                        <div className="mt-2">
                          <input
                            type="text"
                            value={formData.logo && !formData.logo.startsWith('data:') ? formData.logo : ''}
                            onChange={(e) => {
                              const url = e.target.value;
                              handleInputChange('logo', url);
                              if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                                setImagePreview(url);
                              } else if (!url) {
                                setImagePreview(undefined);
                              }
                            }}
                            placeholder="Ou entrez l'URL d'une image externe"
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Icône du site (favicon) <span className="text-gray-500 text-xs">(tenant.config.icon)</span>
                        </label>
                        <input
                          ref={iconInputRef}
                          type="file"
                          accept={ACCEPTED_IMAGE_TYPES.join(',')}
                          onChange={handleIconChange}
                          className="hidden"
                        />
                        
                        {iconPreview ? (
                          <div className="relative w-full h-32 bg-white/10 rounded-lg overflow-hidden">
                            <img
                              src={iconPreview}
                              alt="Icon preview"
                              className="w-full h-full object-contain"
                            />
                            <button
                              type="button"
                              className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-600 text-white p-1 rounded-full"
                              onClick={removeIcon}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div 
                            className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center bg-white/5 hover:border-white/20 transition-colors cursor-pointer"
                            onDrop={handleIconChange}
                            onDragOver={handleDragOver}
                            onClick={() => iconInputRef.current?.click()}
                          >
                            <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                            <p className="text-sm text-gray-400 mb-1">
                              Glissez-déposez une icône ou cliquez pour sélectionner
                            </p>
                            <p className="text-xs text-gray-500">
                              SVG, PNG, ICO (max. 5MB)
                            </p>
                          </div>
                        )}
                        
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => iconInputRef.current?.click()}
                            className="px-3 py-2 bg-white/5 border border-white/10 rounded-md text-gray-300 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors flex items-center justify-center"
                          >
                            <ImageIcon className="h-4 w-4 mr-2" />
                            Uploader
                          </button>
                          <button
                            type="button"
                            onClick={() => openMediaSelector('icon')}
                            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors flex items-center justify-center"
                          >
                            <ImageIcon className="h-4 w-4 mr-2" />
                            Médiathèque
                          </button>
                        </div>
                        
                        <div className="mt-2">
                          <input
                            type="text"
                            value={formData.config.icon && !formData.config.icon.startsWith('data:') ? formData.config.icon : ''}
                            onChange={(e) => {
                              const url = e.target.value;
                              handleConfigChange('icon', url);
                              if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                                setIconPreview(url);
                              } else if (!url) {
                                setIconPreview(undefined);
                              }
                            }}
                            placeholder="Ou entrez l'URL d'une icône externe"
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Background du site <span className="text-gray-500 text-xs">(tenant.config.bg)</span>
                        </label>
                        <input
                          ref={bgInputRef}
                          type="file"
                          accept={ACCEPTED_IMAGE_TYPES.join(',')}
                          onChange={handleBgChange}
                          className="hidden"
                        />
                        
                        {bgPreview ? (
                          <div className="relative w-full h-48 bg-white/10 rounded-lg overflow-hidden">
                            <img
                              src={bgPreview}
                              alt="Background preview"
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-600 text-white p-1 rounded-full"
                              onClick={removeBg}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div 
                            className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center bg-white/5 hover:border-white/20 transition-colors cursor-pointer h-48 flex flex-col items-center justify-center"
                            onDrop={handleBgChange}
                            onDragOver={handleDragOver}
                            onClick={() => bgInputRef.current?.click()}
                          >
                            <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                            <p className="text-sm text-gray-400 mb-1">
                              Glissez-déposez un background ou cliquez pour sélectionner
                            </p>
                            <p className="text-xs text-gray-500">
                              JPG, PNG, WEBP (max. 5MB)
                            </p>
                          </div>
                        )}
                        
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => bgInputRef.current?.click()}
                            className="px-3 py-2 bg-white/5 border border-white/10 rounded-md text-gray-300 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors flex items-center justify-center"
                          >
                            <ImageIcon className="h-4 w-4 mr-2" />
                            Uploader
                          </button>
                          <button
                            type="button"
                            onClick={() => openMediaSelector('bg')}
                            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors flex items-center justify-center"
                          >
                            <ImageIcon className="h-4 w-4 mr-2" />
                            Médiathèque
                          </button>
                        </div>
                        
                        <div className="mt-2">
                          <input
                            type="text"
                            value={formData.config.bg && !formData.config.bg.startsWith('data:') ? formData.config.bg : ''}
                            onChange={(e) => {
                              const url = e.target.value;
                              handleConfigChange('bg', url);
                              if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                                setBgPreview(url);
                              } else if (!url) {
                                setBgPreview(undefined);
                              }
                            }}
                            placeholder="Ou entrez l'URL d'un background externe"
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                          />
                        </div>
                      </div>

                    </div>
                  </div>

                  <TenantGeneralConfig
                    formData={formData}
                    handleConfigChange={handleConfigChange}
                  />
                  <div className="mt-6 text-xs text-gray-600 opacity-50 border-t border-white/5 pt-2">
                    📄 src/components/admin/TenantGeneralConfig.tsx
                  </div>
                </section>
              )}

              {/* Onglet Couleurs & Fonctionnalités */}
              {activeTab === 'appearance' && (
                <TenantColorsAndFeatures
                  formData={formData}
                  handleConfigChange={handleConfigChange}
                />
              )}
              {activeTab === 'appearance' && (
                <div className="mt-6 text-xs text-gray-600 opacity-50 border-t border-white/5 pt-2">
                  📄 src/components/admin/TenantColorsAndFeatures.tsx
                </div>
              )}

              {/* Onglet Pages personnalisées */}
              {activeTab === 'pages' && (
                <TenantPagesCustomization
                  formData={formData}
                  handleConfigChange={handleConfigChange}
                />
              )}
              {activeTab === 'pages' && (
                <div className="mt-6 text-xs text-gray-600 opacity-50 border-t border-white/5 pt-2">
                  📄 src/components/admin/TenantPagesCustomization.tsx
                </div>
              )}

              {/* Onglet Réseaux sociaux */}
              {activeTab === 'social' && (
                <TenantSocialMedia
                  formData={formData}
                  handleConfigChange={handleConfigChange}
                />
              )}
              {activeTab === 'social' && (
                <div className="mt-6 text-xs text-gray-600 opacity-50 border-t border-white/5 pt-2">
                  📄 src/components/admin/TenantSocialMedia.tsx
                </div>
              )}

              {/* Onglet Paiements */}
              {activeTab === 'payments' && (
                <section>
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-white mb-2">Configuration des moyens de paiement</h3>
                    <p className="text-sm text-gray-400">
                      Activez ou désactivez les moyens de paiement disponibles pour ce domaine et définissez leur ordre d'affichage.
                    </p>
                  </div>

                  {isLoadingPayments ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
                      <span className="ml-3 text-gray-400">Chargement des moyens de paiement...</span>
                    </div>
                  ) : paymentConfig.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Aucun moyen de paiement disponible</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paymentConfig.map((provider, index) => (
                        <div
                          key={provider.provider_id}
                          className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                            provider.is_enabled
                              ? 'bg-white/5 border-white/10'
                              : 'bg-black/20 border-white/5 opacity-60'
                          }`}
                        >
                          <div className="flex items-center space-x-4">
                            <div className="flex flex-col space-y-1">
                              <button
                                type="button"
                                onClick={() => movePaymentProvider(index, 'up')}
                                disabled={index === 0}
                                className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <ArrowUp className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => movePaymentProvider(index, 'down')}
                                disabled={index === paymentConfig.length - 1}
                                className="p-1 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <ArrowDown className="h-4 w-4" />
                              </button>
                            </div>
                            
                            {provider.logo_url && (
                              <img
                                src={provider.logo_url}
                                alt={provider.name}
                                className="h-10 w-10 object-contain rounded"
                              />
                            )}
                            
                            <div>
                              <p className="font-medium text-white">{provider.name}</p>
                              <p className="text-xs text-gray-500">ID: {provider.provider_id}</p>
                            </div>
                          </div>

                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={provider.is_enabled}
                              onChange={() => togglePaymentProvider(index)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
                            <span className="ml-3 text-sm text-gray-300">
                              {provider.is_enabled ? 'Activé' : 'Désactivé'}
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={handleSavePaymentConfig}
                      disabled={isSavingPayments || isLoadingPayments}
                      className="flex items-center px-4 py-2 bg-gradient-to-r from-amber-800 to-amber-700 text-white rounded-md hover:from-amber-900 hover:to-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSavingPayments ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Sauvegarde...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Sauvegarder les paiements
                        </>
                      )}
                    </button>
                  </div>
                </section>
              )}

              {/* Onglet Sauvegardes */}
              {activeTab === 'backups' && domainInfo && (
                <TenantConfigBackup
                  tenantId={parseInt(formData.id, 10)}
                  domainId={domainInfo.id}
                  domainName={domainInfo.domain_name}
                  currentConfig={formData.config}
                />
              )}
              {activeTab === 'backups' && (
                <div className="mt-6 text-xs text-gray-600 opacity-50 border-t border-white/5 pt-2">
                  📄 src/components/admin/TenantConfigBackup.tsx
                </div>
              )}

              {/* Boutons d'action */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 text-gray-300 bg-white/5 border border-white/10 rounded-md hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2 bg-gradient-to-r from-amber-800 to-amber-700 text-white rounded-md hover:from-amber-900 hover:to-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Sélecteur de média */}
      <MediaSelector
        isOpen={showMediaSelector}
        onClose={() => setShowMediaSelector(false)}
        onSelect={handleMediaSelect}
        selectedUrl={mediaSelectorType === 'logo' ? imagePreview : mediaSelectorType === 'icon' ? iconPreview : bgPreview}
        title={mediaSelectorType === 'logo' ? 'Sélectionner un logo' : mediaSelectorType === 'icon' ? 'Sélectionner une icône' : 'Sélectionner un background'}
      />
    </AdminLayout>
  );
}